import type {
  AttachComplianceDocumentInput,
  ComplianceDocumentContentType,
  CreateTrailerInput,
  Page,
  PresignComplianceDocumentInput,
  TrailerListQuery,
  TransferAssetOwnershipInput,
  TruckComplianceStatus,
  UpdateTrailerInput,
} from '@chukua/common';
import { api } from '../../../../shared/shared.api';
import {
  type ComplianceDocumentView,
  type PresignedUpload,
  uploadToPresignedUrl,
} from '../../../../shared/features/compliance-documents/policies/compliance-documents';
import { toListParams } from '../../../../shared/policies/list-params';

export interface TrailerListItem {
  id: string;
  registration: string;
  typeCode: string;
  capacityTonnes: number;
  axleCount: number;
  compliance: TruckComplianceStatus;
  isActive: boolean;
  createdAt: string;
}

export interface TrailerView {
  id: string;
  registration: string;
  typeCode: string;
  capacityTonnes: number;
  axleCount: number;
  isSkeletal: boolean;
  isFlatRack: boolean;
  isReefer: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TrailerDetail extends TrailerView {
  documents: ComplianceDocumentView[];
}

export function listTrailers(query: TrailerListQuery): Promise<Page<TrailerListItem>> {
  return api<Page<TrailerListItem>>(`/trailers?${toListParams(query)}`);
}

export async function createTrailer(input: CreateTrailerInput): Promise<void> {
  await api<void>('/trailers', { method: 'POST', body: JSON.stringify(input) });
}

export function getTrailer(id: string): Promise<TrailerDetail> {
  return api<TrailerDetail>(`/trailers/${id}`);
}

export async function updateTrailer(id: string, input: UpdateTrailerInput): Promise<void> {
  await api<void>(`/trailers/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function activateTrailer(id: string): Promise<void> {
  await api<void>(`/trailers/${id}/activate`, { method: 'POST' });
}

export async function deactivateTrailer(id: string): Promise<void> {
  await api<void>(`/trailers/${id}/deactivate`, { method: 'POST' });
}

export interface TrailerOwnershipPeriod {
  id: string;
  ownerId: string;
  ownerName: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export function listTrailerOwnershipPeriods(trailerId: string): Promise<TrailerOwnershipPeriod[]> {
  return api<TrailerOwnershipPeriod[]>(`/trailers/${trailerId}/ownership-periods`);
}

export async function transferTrailerOwnership(trailerId: string, input: TransferAssetOwnershipInput): Promise<void> {
  await api<void>(`/trailers/${trailerId}/ownership-periods/transfer`, { method: 'POST', body: JSON.stringify(input) });
}

export function presignTrailerDocument(trailerId: string, input: PresignComplianceDocumentInput): Promise<PresignedUpload> {
  return api<PresignedUpload>(`/trailers/${trailerId}/documents/presign`, { method: 'POST', body: JSON.stringify(input) });
}

/** Presign then upload in one call, so a hook only awaits the result — the key and content type it wrote to. */
export async function uploadTrailerDocumentFile(trailerId: string, file: File): Promise<{ key: string; contentType: ComplianceDocumentContentType }> {
  const contentType = file.type as ComplianceDocumentContentType;
  const upload = await presignTrailerDocument(trailerId, { contentType });
  await uploadToPresignedUrl(upload, file);
  return { key: upload.key, contentType };
}

/** Add or renew — the server resolves which by whether a current document already exists for this type. Callers reload the trailer detail afterward. */
export async function attachTrailerDocument(trailerId: string, input: AttachComplianceDocumentInput): Promise<void> {
  await api<void>(`/trailers/${trailerId}/documents`, { method: 'POST', body: JSON.stringify(input) });
}

export function getTrailerDocumentDownloadUrl(trailerId: string, documentId: string): Promise<{ url: string }> {
  return api<{ url: string }>(`/trailers/${trailerId}/documents/${documentId}/download-url`);
}
